import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NotificationService } from '@appkit4/angular-components/notification';
import { TranslateService } from '@ngx-translate/core';
import { notificationDataOptions } from '@shared/helpers/static/notification-data-options';
import { catchError, Observable, throwError } from 'rxjs';
import { ProblemDetails, ValidationProblemDetails } from '../web-api-client';

@Injectable()
export class ErrorsHandlerInterceptor implements HttpInterceptor {
  private readonly translate = inject(TranslateService);

  private notificationService = inject(NotificationService);
  private lastForbiddenToastAt = 0;

  intercept<T>(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<T | never>> {
    return this.getHandle(req, next);
  }
  private getHandle<T>(
    req: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<T>> {
    const isAuthUrl = /authenticationlink|accesstoken|oauth-loginsession/.test(
      req.url
    );
    // Permission probing is optional and has a fallback, so it must stay silent
    const isSilentUrl = /countryadminpermissions/i.test(req.url);
    if (isAuthUrl) {
      return next.handle(req);
    }
    if (isSilentUrl) {
      return next.handle(req).pipe(
        catchError(errorDetails => this.parseErrorBlob(errorDetails)),
        catchError(errorDetails => throwError(() => errorDetails))
      );
    }
    return next.handle(req).pipe(
      catchError(errorDetails => this.parseErrorBlob(errorDetails)),
      catchError(errorDetails => this.errorHandler(errorDetails))
    );
  }
  private errorHandler(
    errorDetails: ProblemDetails | ValidationProblemDetails
  ): Observable<never> {
    const isForbidden = errorDetails.status === HttpStatusCode.Forbidden;
    let title = this.translate.instant('error-notifications.title');
    let description = this.generateErrorDescription(errorDetails);

    if (errorDetails.status === HttpStatusCode.InternalServerError) {
      title = this.translate.instant('error-notifications.server-error.title');
    } else if (isForbidden) {
      title = this.translate.instant('error-notifications.forbidden.title');
      description = this.translate.instant(
        'error-notifications.forbidden.description'
      );
    }

    return throwError(() => {
      if (isForbidden && !this.shouldShowForbiddenToast()) {
        return;
      }

      this.notificationService.show(
        title,
        description,
        '',
        '',
        notificationDataOptions
      );
    });
  }

  private shouldShowForbiddenToast(): boolean {
    const now = Date.now();
    if (now - this.lastForbiddenToastAt < 2000) {
      return false;
    }

    this.lastForbiddenToastAt = now;
    return true;
  }

  parseErrorBlob(errorResponse: HttpErrorResponse): Observable<never> {
    const reader: FileReader = new FileReader();

    const obs = new Observable<never>(observer => {
      reader.onloadend = (): void => {
        try {
          observer.error(JSON.parse(reader.result as string));
        } catch {
          observer.error(new Error(reader.result as string));
        }
        observer.complete();
      };
    });
    reader.readAsText(errorResponse.error);
    return obs;
  }

  generateErrorDescription(
    errorDetails: ProblemDetails | ValidationProblemDetails
  ): string {
    let message: string = errorDetails.title ?? '';
    if ('errors' in errorDetails && errorDetails.errors) {
      const errorList: { [key: string]: string[] } = errorDetails.errors;
      Object.keys(errorList).forEach(key => {
        const errorProperty = errorList[key].reduce((prev, curr) => {
          return `${prev}, ${curr}`;
        });
        message = `${message} ${key}: ${errorProperty}`;
      });
    }

    return message;
  }
}
